import type { EntryType, MoneyString, Transaction } from './types';

const CURRENCY_DIGITS: Record<string, number> = { THB: 2, USD: 2, EUR: 2, GBP: 2, JPY: 0 };

export function currencyDigits(currency: string): number {
  return CURRENCY_DIGITS[currency.toUpperCase()] ?? 2;
}

export function parseMoney(input: string, currency = 'THB'): MoneyString {
  const value = input.trim().replace(/,/g, '');
  const digits = currencyDigits(currency);
  const pattern = digits === 0 ? /^\d+$/ : new RegExp(`^\\d+(?:\\.\\d{0,${digits}})?$`);
  if (!pattern.test(value)) throw new Error(`Enter a positive amount with up to ${digits} decimal places.`);
  const [whole, fraction = ''] = value.split('.');
  const minor = BigInt(whole) * 10n ** BigInt(digits) + BigInt((fraction + '0'.repeat(digits)).slice(0, digits) || '0');
  if (minor <= 0n) throw new Error('Amount must be greater than zero.');
  if (minor > 9_000_000_000_000_000n) throw new Error('Amount is too large.');
  return minor.toString();
}

const formatters = new Map<string, { money: Intl.NumberFormat; fraction: Intl.NumberFormat }>();

export function formatMoney(minor: MoneyString | bigint, currency = 'THB', locale?: string): string {
  const digits = currencyDigits(currency);
  const key = `${locale ?? ''}:${currency}:${digits}`;
  let format = formatters.get(key);
  if (!format) {
    format = {
      money: new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits }),
      fraction: new Intl.NumberFormat(locale, { useGrouping: false, minimumIntegerDigits: Math.max(1, digits) })
    };
    // Bound the cache even if a future import contains many currency/locale pairs.
    if (formatters.size >= 32) formatters.delete(formatters.keys().next().value!);
    formatters.set(key, format);
  }
  const amount = BigInt(minor);
  const absolute = amount < 0n ? -amount : amount;
  const scale = 10n ** BigInt(digits);
  const whole = absolute / scale;
  const signedWhole = amount < 0n ? (whole === 0n ? -0 : -whole) : whole;
  // Format the integer directly as bigint; converting money to Number loses cents.
  return format.money.formatToParts(signedWhole).map((part) =>
    part.type === 'fraction' ? format.fraction.format(absolute % scale) : part.value
  ).join('');
}

export function addMoney(...values: Array<MoneyString | bigint>): MoneyString {
  return values.reduce<bigint>((sum, value) => sum + BigInt(value), 0n).toString();
}

export function signedAmount(type: EntryType, amountMinor: MoneyString): bigint {
  if (type === 'expense') return BigInt(amountMinor);
  if (type === 'refund') return -BigInt(amountMinor);
  return 0n;
}

export function totals(transactions: Transaction[]) {
  let gross = 0n;
  let refunds = 0n;
  for (const tx of transactions) {
    if (tx.deletedAt) continue;
    if (tx.type === 'expense') gross += BigInt(tx.amountMinor);
    if (tx.type === 'refund') refunds += BigInt(tx.amountMinor);
  }
  return { grossMinor: gross.toString(), refundsMinor: refunds.toString(), netMinor: (gross - refunds).toString() };
}

export function validateAllocations(amountMinor: MoneyString, allocations: Array<{ amountMinor: MoneyString }>): void {
  const sum = allocations.reduce((value, item) => value + BigInt(item.amountMinor), 0n);
  if (sum !== BigInt(amountMinor)) throw new Error('Category allocations must add up exactly to the total.');
  if (allocations.some((item) => BigInt(item.amountMinor) <= 0n)) throw new Error('Each allocation must be positive.');
}
